import { useMemo, useState } from "react";
import { programmeringByMin } from "../../lib/data";
import { Icon } from "../Icon";
import { useNow } from "../../hooks/useNow";
import { isCurrent, minutesNow } from "../../lib/time";

export function AandachtspuntenlijstView({ onBack }: { onBack: () => void }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "with-aandacht" | "live">("all");
  const now = useNow(20_000);
  const nowMin = minutesNow(now);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return programmeringByMin.filter((p) => {
      if (filter === "with-aandacht" && !p.has_aandacht) return false;
      if (
        filter === "live" &&
        !isCurrent(p.vanMin, p.totMin, nowMin)
      )
        return false;
      if (!term) return true;
      return (
        p.wat.toLowerCase().includes(term) ||
        (p.locatie.raw?.toLowerCase().includes(term) ?? false) ||
        (p.aandachtspunten?.toLowerCase().includes(term) ?? false)
      );
    });
  }, [q, filter, nowMin]);

  const counts = {
    all: programmeringByMin.length,
    aandacht: programmeringByMin.filter((p) => p.has_aandacht).length,
    live: programmeringByMin.filter((p) =>
      isCurrent(p.vanMin, p.totMin, nowMin),
    ).length,
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start gap-2 mb-3">
        <button
          onClick={onBack}
          aria-label="Terug naar documenten"
          className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-zinc-400 active:bg-ink-700 hover:text-zinc-100"
        >
          ‹
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-100 text-base truncate">
            Aandachtspuntenlijst
          </div>
          <div className="text-2xs text-zinc-500 mt-0.5 truncate">
            Programmering + aandachtspunten per act
          </div>
        </div>
        <a
          href="/docs/aandachtspuntenlijst.xlsx"
          download
          aria-label="Download Excel"
          className="shrink-0 px-3 h-9 rounded-lg border border-line text-zinc-300 text-2xs flex items-center hover:bg-ink-700 active:bg-ink-700"
        >
          XLSX
        </a>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none">
          <Icon.Search className="w-4 h-4" />
        </span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Zoek act, locatie of aandachtspunt…"
          aria-label="Zoek in aandachtspuntenlijst"
          className="w-full bg-ink-900 border border-white/[0.08] rounded-lg pl-9 pr-9 py-2.5 text-sm placeholder:text-zinc-500 focus:outline-none focus:border-accent/60"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            aria-label="Wis"
            className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center text-zinc-500 hover:text-zinc-100 active:bg-ink-700 rounded"
          >
            <Icon.Close className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 mb-3 text-xs">
        <FilterChip
          label="Alle"
          count={counts.all}
          active={filter === "all"}
          onClick={() => setFilter("all")}
        />
        <FilterChip
          label="Met aandacht"
          count={counts.aandacht}
          active={filter === "with-aandacht"}
          onClick={() => setFilter("with-aandacht")}
        />
        <FilterChip
          label="Live nu"
          count={counts.live}
          active={filter === "live"}
          onClick={() => setFilter("live")}
        />
      </div>

      {/* Table-like rows */}
      <ul className="space-y-1">
        {filtered.map((p) => {
          const live = isCurrent(p.vanMin, p.totMin, nowMin);
          return (
            <li
              key={p.id}
              className={`card p-3 ${
                p.has_aandacht ? "border-warn/30" : ""
              } ${live ? "border-ok/40" : ""}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-12 shrink-0 text-2xs tabular text-zinc-300 font-mono">
                  {p.tijd_van ?? "NTB"}
                  {p.tijd_tot && (
                    <div className="text-zinc-600">
                      {p.tijd_tot}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    {live && (
                      <span className="pill bg-ok/15 text-ok">
                        <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulseSoft" />
                        Live
                      </span>
                    )}
                    {p.has_aandacht && (
                      <span className="pill bg-warn/15 text-warn">⚠ aandacht</span>
                    )}
                    {p.is_overleg && (
                      <span className="pill bg-zinc-800 text-zinc-300">ECC</span>
                    )}
                  </div>
                  <div className="font-medium text-zinc-100 text-sm">
                    {p.wat}
                  </div>
                  {p.locatie.venue && (
                    <div className="text-2xs text-zinc-500 mt-0.5">
                      {p.locatie.venue}
                      {p.locatie.stage ? ` · ${p.locatie.stage}` : ""}
                    </div>
                  )}
                  {p.aandachtspunten && (
                    <div className="text-2xs text-zinc-300 mt-2 whitespace-pre-wrap leading-relaxed border-l-2 border-warn/40 pl-2">
                      {p.aandachtspunten}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
        {filtered.length === 0 && (
          <li className="text-2xs text-zinc-500 px-1 py-6 text-center">
            Geen resultaten.
          </li>
        )}
      </ul>
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`px-3 py-1.5 min-h-[36px] rounded-full border transition-colors ${
        active
          ? "bg-accent/15 border-accent/40 text-accent"
          : "border-line text-zinc-400 hover:text-zinc-200 active:bg-ink-700"
      }`}
    >
      {label}{" "}
      <span className="opacity-60 tabular ml-0.5">{count}</span>
    </button>
  );
}
